import { Box, Divider, InputAdornment, styled, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import theme from "../../../theme";

const COMPACT_FIELD = {
  labelMinHeight: "32px",
  inputHeight: "48px",
  tableInputHeight: "44px",
  helperMinHeight: "16px",
  labelMarginBottom: "4px",
};

const compactHelperSx = {
  minHeight: COMPACT_FIELD.helperMinHeight,
  margin: "3px 0 0",
  lineHeight: COMPACT_FIELD.helperMinHeight,
  display: "block",
};

const compactLabelSx = (theme) => ({
  fontSize: theme.typography.regular.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  minHeight: COMPACT_FIELD.labelMinHeight,
  lineHeight: 1.25,
  marginBottom: COMPACT_FIELD.labelMarginBottom,
  display: "flex",
  alignItems: "flex-end",
  color: theme.palette.custom.productTextColor,
});

const compactOutlinedInputSx = (theme, height = COMPACT_FIELD.inputHeight) => ({
  height,
  minHeight: height,
  maxHeight: height,
  borderRadius: theme.borderRadius.radius.wallet,
  background: theme.palette.custom.white,
  backgroundColor: theme.palette.custom.white,
  boxSizing: "border-box",
  padding: "0 10px !important",
  "&.Mui-error": {
    background: theme.palette.custom.white,
    backgroundColor: theme.palette.custom.white,
  },
  "& .MuiInputBase-input": {
    padding: "0 !important",
    height: "24px",
    minHeight: "24px",
    lineHeight: "24px",
    boxSizing: "border-box",
    backgroundColor: "transparent",
  },
});

const profileInputRoot = (theme, fieldHeight) =>
  compactOutlinedInputSx(theme, fieldHeight);

export const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    !["isprofile", "tableField", "formGrid", "profileForm", "isDollar"].includes(prop),
})(({ theme, height, isDollar, isprofile, tableField, profileForm }) => {
    const fieldHeight = tableField
      ? COMPACT_FIELD.tableInputHeight
      : COMPACT_FIELD.inputHeight;
    const useProfileSizing = isprofile || tableField;
    return {
    width: "100%",
    height: "100%",
    cursor:'pointer',
    background: "transparent",
    ...(useProfileSizing && {
      "& .MuiOutlinedInput-root": profileInputRoot(theme, fieldHeight),
    }),
    "& .MuiInputBase-input": {
      fontSize: theme.typography.regular.fontSize,
      height: useProfileSizing ? "24px" : height ? height : "100%",
      minHeight: useProfileSizing ? "24px" : undefined,
      lineHeight: useProfileSizing ? "24px" : undefined,
      paddingTop: useProfileSizing ? "0" : "17px",
      paddingBottom: useProfileSizing ? "0" : "17px",
      paddingLeft: useProfileSizing ? "0" : "10px",
      paddingRight: useProfileSizing ? "0" : undefined,
      background: "transparent",
      backgroundColor: "transparent",
      borderRadius: theme.borderRadius.radius.wallet,
      boxSizing: "border-box",
    },
    ...(isDollar && {
      ".MuiOutlinedInput-root": {
        paddingLeft: "0px !important",
      },
    }),
    "& .MuiInputBase-root-MuiOutlinedInput-root": {
      borderRadius: theme.borderRadius.radius.wallet,
    },
    "& .MuiInputBase-multiline": {
      padding: "0px 16px !important",
      background: theme.palette.custom.white,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      ...(useProfileSizing ? compactHelperSx : { minHeight: "20px" }),
    },
    "& .MuiInputBase-root input:-webkit-autofill": {
      boxShadow: "0 0 0 1000px white inset",
      WebkitTextFillColor: "#000",
      transition: "background-color 5000s ease-in-out 0s",
      caretColor: "#000",
    },
    ...(useProfileSizing && {
      '& input[type="date"]': {
        lineHeight: "24px",
      },
    }),
  };
  },
);
export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isprofile" &&
    prop !== "issize" &&
    prop !== "formGrid" &&
    prop !== "tableField" &&
    prop !== "profileForm",
})(({ theme, isprofile, issize, formGrid, tableField, profileForm }) => ({
  paddingTop:
    formGrid || tableField || profileForm
      ? "0px"
      : isprofile
        ? "35px"
        : issize
          ? "0px"
          : theme.padding.size.large,
  width: "100%",
  ...(formGrid && {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  }),
}));
export const StyledFormControlSlot = styled(Box)(() => ({
  width: "100%",
  flexShrink: 0,
  background: "transparent",
}));
export const StyledBoxLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "formGrid",
})(({ theme, color, fontSize, formGrid }) => ({
  fontSize: fontSize ? fontSize : theme.typography.extraLarge.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  marginBottom: "6px",
  color: color || theme.palette.custom.productTextColor,
  flexShrink: 0,
  ...(formGrid ? compactLabelSx(theme) : {}),
}));
export const StyledBoxStyle = styled(Box)(() => ({
  paddingTop: "0px",
  width: "100%",
}));
export const StyledSearchIcon = styled(SearchIcon)(() => ({
  width: "17px",
  height: "17px",
}));
export const StyledDivider = styled(Divider)(() => ({
  marginBottom: "14px",
}));
export const StyledLabelSpan = styled("span")(({ theme }) => ({
  color: theme.palette.custom.danger,
}));

export const StyledOrdersContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.custom.white,
  borderRadius: theme.borderRadius.radius.regular,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  border: theme.borders.highlighted,
  paddingBottom: "50px",
  marginBottom: "100px",
}));
export const StyledBoxCountry = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "40px",
  background: theme.palette.custom.white,
  zIndex: "99999",
}));
export const StyledBoxCode = styled(Box)(({ theme }) => ({
  paddingLeft: "20px",
  color: theme.palette.custom.blackColor,
}));
export const StyledBoxName = styled(Box)(({ theme }) => ({
  paddingRight: "8px",
  color: theme.palette.custom.blackColor,
}));
export const StyledImg = styled("img")(() => ({
  cursor: "pointer",
}));
export const StyledInputAdornment = styled(InputAdornment)(() => ({
  position: "relative",
  background: theme.palette.custom.white,
}));

export const CommonInputProps = {
  style: {
    borderRadius: theme.borderRadius.radius.wallet,
    background: theme.palette.custom.white,
    padding: "0px !important",
  },
};
